import type {
  EquipmentAdmission,
  EquipmentLibraryBundle,
  EquipmentObservation,
  EquipmentProduct,
  EquipmentRevision,
  EquipmentSource,
  EquipmentSourceKind,
} from '../../types/equipmentLibrary';
import { EQUIPMENT_LIBRARY_SCHEMA_VERSION } from '../../types/equipmentLibrary';
import {
  sealEquipmentAdmission,
  sealEquipmentLibraryBundle,
  sealEquipmentObservation,
  sealEquipmentProduct,
  sealEquipmentRevision,
  sealEquipmentSource,
} from '../../lib/equipmentLibrary/digests';
import {
  CURATED_OEM_SEED,
  CURATED_OEM_SEED_COMPILED_AT,
  CURATED_OEM_SEED_SOURCES,
} from './curatedOemSeed';
import type {
  CuratedOemSeedRecord,
  EquipmentEvidenceSource,
  SourceKind,
} from './seedSchema';
import { toCanonicalProductDraft } from './seedSchema';
import { assertValidCuratedOemSeed } from './validateCuratedSeed';

const CURATOR_ID = 'deep-atomic-equipment-curation';

function canonicalSourceKind(kind: SourceKind): EquipmentSourceKind {
  switch (kind) {
    case 'oem-product-page':
      return 'product-page';
    case 'oem-portfolio-page':
      return 'product-guide';
    case 'oem-datasheet':
      return 'datasheet';
    case 'oem-brochure':
      return 'product-guide';
    case 'regulatory-registry':
      return 'certified-registry';
    case 'government-dataset':
      return 'technical-report';
    case 'open-source-model':
      return 'technical-report';
  }
}

function compileSource(source: EquipmentEvidenceSource): EquipmentSource {
  return sealEquipmentSource({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: source.id,
    kind: canonicalSourceKind(source.kind),
    publisher: source.publisher,
    title: source.title,
    retrievedAt: source.lastVerifiedOn,
    access: 'public',
    visibility: 'public',
    url: source.url,
    license: source.rights.licenseOrTerms,
    notes: [
      `Allowed use: ${source.rights.allowedUse}`,
      `Prohibited use: ${source.rights.prohibitedUse}`,
      source.notes,
    ].filter((value): value is string => Boolean(value)).join(' '),
  });
}

function revisionId(record: CuratedOemSeedRecord): string {
  return `${record.id}:reviewed-2026q3`;
}

function identityObservationId(record: CuratedOemSeedRecord): string {
  return `${record.id}:identity:2026q3`;
}

function compileProduct(record: CuratedOemSeedRecord): EquipmentProduct {
  const draft = toCanonicalProductDraft(record);
  return sealEquipmentProduct({
    ...draft,
    origin: record.evidenceTier === 'tier-2-regulatory-registry'
      ? 'public-registry'
      : 'oem-publication',
    visibility: record.evidenceTier === 'tier-2-regulatory-registry'
      ? 'public-registry'
      : 'platform',
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    currentRevisionId: revisionId(record),
    createdAt: CURATED_OEM_SEED_COMPILED_AT,
    updatedAt: CURATED_OEM_SEED_COMPILED_AT,
  }) as EquipmentProduct;
}

function compileRevision(record: CuratedOemSeedRecord): EquipmentRevision {
  return sealEquipmentRevision({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: revisionId(record),
    productId: record.id,
    revisionLabel: 'reviewed-2026q3',
    variantLabel: record.family,
    status: 'reviewed',
    configurationLevel: record.classification.level,
    frequencyHz: record.frequencyHz,
    fuels: [],
    duties: record.classification.applications,
    sourceIds: record.sourceIds,
    observationIds: [identityObservationId(record)],
    compiledAt: CURATED_OEM_SEED_COMPILED_AT,
    reviewedAt: CURATED_OEM_SEED_COMPILED_AT,
    reviewedBy: CURATOR_ID,
    notes: record.dataGaps.length > 0
      ? `Known gaps: ${record.dataGaps.join('; ')}`
      : undefined,
  });
}

function compileIdentityObservation(
  record: CuratedOemSeedRecord,
): EquipmentObservation {
  return sealEquipmentObservation({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: identityObservationId(record),
    revisionId: revisionId(record),
    metric: 'custom:documented-product-family',
    value: {
      kind: 'text',
      value: `${record.manufacturer} ${record.family}`,
    },
    unit: '1',
    basis: {
      qualifier: 'nominal',
      configuration: record.family,
      notes: 'Identity-only observation; no technical performance value is inferred.',
    },
    sourceRefs: record.sourceIds.map(sourceId => ({
      sourceId,
      locator: 'Product or capability-family identity',
    })),
    evidenceStatus: record.evidenceTier === 'tier-2-regulatory-registry'
      ? 'manufacturer-submitted'
      : 'published',
    confidence: record.evidenceTier === 'tier-1-oem-primary' ? 'high' : 'medium',
    reviewedAt: CURATED_OEM_SEED_COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
}

function compileBrowseAdmission(record: CuratedOemSeedRecord): EquipmentAdmission {
  const researchOnly = record.catalogStatus !== 'active'
    || record.lifecycleStatus !== 'commercial-current';
  return sealEquipmentAdmission({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: `${record.id}:browse:2026q3`,
    revisionId: revisionId(record),
    surface: 'browse',
    status: researchOnly ? 'comparison-only' : 'admitted',
    requiredObservationIds: [identityObservationId(record)],
    missingMetrics: [],
    conditions: [
      `Evidence tier: ${record.evidenceTier}`,
      ...(researchOnly ? ['Research-only; not admitted for screening or simulation.'] : []),
    ],
    reason: researchOnly
      ? 'Documented OEM identity is visible for research comparison; commercial maturity is not yet sufficient for project screening.'
      : 'Manufacturer/family identity and current source were manually reviewed for library visibility.',
    reviewedAt: CURATED_OEM_SEED_COMPILED_AT,
    reviewedBy: CURATOR_ID,
  });
}

/**
 * Reproducible, facts-only Q3 2026 snapshot.
 *
 * Sparse source-backed identities receive no fabricated observations, market
 * prices, lead times, or solver admissions. Those facts arrive later as their
 * own immutable evidence records.
 */
export function buildCuratedEquipmentLibraryBundle(): EquipmentLibraryBundle {
  assertValidCuratedOemSeed(CURATED_OEM_SEED);

  const sources = CURATED_OEM_SEED_SOURCES.map(compileSource);
  const products = CURATED_OEM_SEED.map(compileProduct);
  const revisions = CURATED_OEM_SEED.map(compileRevision);
  const observations = CURATED_OEM_SEED.map(compileIdentityObservation);
  const admissions = CURATED_OEM_SEED.map(compileBrowseAdmission);

  return sealEquipmentLibraryBundle({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    compiledAt: CURATED_OEM_SEED_COMPILED_AT,
    sources,
    products,
    revisions,
    observations,
    admissions,
    marketEvidence: [],
    companyPatches: [],
    legacyIds: [],
    planningBenchmarks: [],
  });
}

export const CURATED_EQUIPMENT_LIBRARY_BUNDLE =
  buildCuratedEquipmentLibraryBundle();
